import { useEffect, useState } from 'react'
import { api } from '../api/client'

export interface RateRow {
  product_name: string
  rate: number
  lender_name: string
  lender: string
  tier?: number | null
  /** ASIC comparison rate as a FRACTION (e.g. 0.0644), or null when not yet set. */
  comparison_rate?: number | null
}

export interface RatesResponse {
  as_at: string
  is_stale: boolean
  rates: RateRow[]
  major?: RateRow[]
  carousel?: RateRow[]
  /** How many rate rows a box shows before scrolling (configured in /dataentry, default 5). */
  visible_count?: number
}

/**
 * The Greystone Container Apps run scale-to-zero, so the first request after an
 * idle period triggers a cold start (~10-20s). Retry with backoff so that
 * wake-up is invisible to the visitor — the strip shows a skeleton and quietly
 * fills in — instead of giving up and flashing "rates unavailable" (issue I5).
 * Delays collapse to ~0 under test so the suite stays fast.
 */
const env = (import.meta as { env?: Record<string, unknown> }).env ?? {}
const IS_TEST = env.MODE === 'test' || Boolean(env.VITEST)
const RETRY_DELAYS_MS = IS_TEST ? [0, 0, 0] : [1500, 3000, 5000, 8000, 12000]

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Shared loader for GET /api/loans/rates. Returns { data, error, loading };
 * `loading` stays true across cold-start retries so callers can render a
 * skeleton rather than an error.
 */
export function useRates() {
  const [data, setData] = useState<RatesResponse | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      for (let attempt = 0; !cancelled; attempt++) {
        try {
          const d = await api.get<RatesResponse>('/api/loans/rates')
          if (!cancelled) setData(d)
          return
        } catch {
          if (cancelled) return
          if (attempt >= RETRY_DELAYS_MS.length) {
            setError(true)
            return
          }
          await sleep(RETRY_DELAYS_MS[attempt])
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return { data, error, loading: !data && !error }
}
